import React, { Component } from "react";
import axios from "axios";
import { Form, Col, Row } from "react-bootstrap";
import * as apiMethods from "../res/apiMethods.js";
import FileSaver from "file-saver";
import readXlsxFile from "read-excel-file";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import AccessDenied from "./accessDenied.jsx";

class uvr extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filename: "",
      file: "",
      uvrLoads: [],
      offset: 0,
      count: 0,
      isUploading: false,
      lastUploadDate: null,
    };
  }

  componentDidMount() {
    document.title = "UVR";
    this.loadInitialData();
  }

  loadInitialData = async () => {
    try {
      const token = window.sessionStorage.getItem("localToken");
      
      // Obtener información inicial del backend
      const response = await axios.get(apiMethods.UVR_INFO, {
        params: { offset: this.state.offset },
        headers: { Authorization: token },
      });

      if (response.data.code === 0) {
        this.setState({
          lastUploadDate: response.data.lastUploadDate,
          uvrLoads: response.data.uvrs,
          count: response.data.count,
        });
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar la información inicial",
      });
    }
  };

  handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    this.setState({ file, filename: file.name }, () => {
      this.load();
    });
  };

  download = (e) => {
    e.preventDefault();
    FileSaver.saveAs(
      process.env.PUBLIC_URL + "/EJEMPLO_CARGA_EXCEL.xlsx",
      "EJEMPLO_CARGA_EXCEL.xlsx"
    );
  };

  load = async (e) => {
    if (e) e.preventDefault();
    const { file } = this.state;
    if (!file) return Swal.fire({ icon: "warning", text: "Selecciona un archivo" });

    const token = window.sessionStorage.getItem("localToken");

    this.setState({ isUploading: true });
    Swal.fire({
      title: "Cargando UVR",
      html: `<div>Procesando archivo...</div>`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      // Leer el archivo Excel
      const rows = await readXlsxFile(file);
      
      if (!rows || rows.length === 0) {
        throw new Error("El archivo está vacío.");
      }

      // Validación básica de estructura en frontend
      const badLenIdx = rows.findIndex((r) => !r || r.length < 2);
      if (badLenIdx !== -1) {
        throw new Error(`Fila ${badLenIdx + 1}: se requieren 2 columnas (fecha, valor).`);
      }

      // Preparar datos para enviar al backend
      const uvrs = rows.map((r, i) => {
        const rawDate = r[0];
        const rawVal = r[1];

        // Validar que la fecha sea un objeto Date
        let fecha;
        if (rawDate instanceof Date && !isNaN(rawDate)) {
          fecha = rawDate.toISOString();
        } else {
          throw new Error(
            `Fila ${i + 1}: la fecha debe ser una CELDA de tipo fecha en Excel (formato fecha corta).`
          );
        }

        // Validar el valor
        let valor;
        if (typeof rawVal === "number" && Number.isFinite(rawVal)) {
          valor = rawVal;
        } else if (typeof rawVal === "string") {
          const s = rawVal.trim();
          const hasDotDecimal = /-?\d+\.\d+/.test(s.replace(/\s/g, ""));
          if (hasDotDecimal) {
            throw new Error(
              `Fila ${i + 1}: el valor debe usar COMA como separador decimal (ej: 1.234,56), no punto.`
            );
          }
          const normalized = s.replace(/\./g, "").replace(/,/g, ".");
          valor = parseFloat(normalized);
          if (!Number.isFinite(valor)) {
            throw new Error(`Fila ${i + 1}: valor inválido.`);
          }
        } else {
          throw new Error(`Fila ${i + 1}: valor inválido.`);
        }

        return { fecha, valor };
      });

      // Enviar al backend para validación y carga
      const response = await axios.post(
        apiMethods.UVR_UPLOAD_BATCH,
        { uvrs },
        { headers: { Authorization: token } }
      );

      if (response.data.code === 0) {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.data.message,
          html: `
            <p>${response.data.message}</p>
            <p>Creados: ${response.data.results.created}</p>
            <p>Actualizados: ${response.data.results.updated}</p>
            ${response.data.results.errors.length > 0 
              ? `<p style="color: red;">Errores: ${response.data.results.errors.length}</p>` 
              : ''}
          `,
        });
        this.setState({ isUploading: false, file: null, filename: "" });
        this.loadInitialData(); // Recargar datos
      } else {
        throw new Error(response.data.message || "Error en la carga");
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error de validación",
        text: err.response?.data?.message || err.message || "Error durante la carga.",
      });
    } finally {
      this.setState({ isUploading: false });
    }
  };

  render = () => {
    const columns = [
      {
        field: "fecha",
        headerName: "Fecha",
        type: "date",
        valueFormatter: (value) => {  
          if (!value) {
            return '-';
          }
          let d = new Date(value); 
          if (isNaN(d.getTime())) {
            return '-';
          }
          const valueFormatted =
            ("0" + d.getUTCDate()).slice(-2) +
            "-" +
            ("0" + (d.getUTCMonth() + 1)).slice(-2) +
            "-" +
            d.getUTCFullYear();
          
          return valueFormatted;
        },
        width: 200,
      },
      { field: "valor", headerName: "Valor", width: 200 },
    ];

    const setPage = async (page) => {
      Swal.fire({ title: "Cargando", allowOutsideClick: false });
      Swal.showLoading();
      let offset = 15 * page;
      
      try {
        const token = window.sessionStorage.getItem("localToken");
        const response = await axios.get(apiMethods.UVR_INFO, {
          params: { offset },
          headers: { Authorization: token },
        });
        
        if (response.data.code === 0) {
          const uvr = [...this.state.uvrLoads, ...response.data.uvrs];
          this.setState({ offset, uvrLoads: uvr });
        }
      } catch (error) {
        console.error("Error al cargar página:", error);
      } finally {
        Swal.close();
      }
    };

    const token = window.sessionStorage.getItem("localToken");
    if (!token) {
      return (
        <React.Fragment>
          <AccessDenied />
        </React.Fragment>
      );
    }

    let decodedPayload;
    try {
      decodedPayload = JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      return (
        <React.Fragment>
          <AccessDenied />
        </React.Fragment>
      );
    }

    if (decodedPayload.roleId !== 1) {
      return (
        <React.Fragment>
          <AccessDenied />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <div style={{ display: "grid", gridTemplateColumns: "15% 85%" }}>
          <Sidebar />
          <div className="card">
            <form>
              <Form.Label className="formHeader">
                Carga de archivo con tasas de conversión UVR
              </Form.Label>
              <br />
              <br />
              <p>
                El archivo debe ser un documento tipo XLSX con dos columnas, <b>fecha</b> y{" "}
                <b>valor</b>. Las columnas no deben tener títulos. El <b>valor</b> debe usar{" "}
                <b>coma (,)</b> como separador decimal y estar en tipo de dato numérico. La{" "}
                <b>fecha</b> debe ser una <b>celda de tipo fecha</b> (formato fecha corta de
                Excel). Las filas deben venir
                <b> en orden descendente</b> por fecha (más reciente primero).
              </p>
              <br />
              <Row>
                <Col md="6">
                  <div className="custom-file">
                    <input
                      type="file"
                      className="custom-file-input"
                      id="customFileLang"
                      lang="es"
                      accept=".xlsx"
                      onChange={this.handleFileSelect}
                      disabled={this.state.isUploading}
                    />
                    <label className="custom-file-label" htmlFor="customFileLang">
                      {this.state.filename || "Seleccionar Archivo"}
                    </label>
                  </div>
                </Col>
              </Row>
              <br />
              {this.state.lastUploadDate && (
                <p>Fecha UVR más reciente: {this.state.lastUploadDate}</p>
              )}
              <br />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "10% 30%",
                  columnGap: "2rem",
                  marginBottom: "2rem",
                }}
              >
                <label htmlFor="customFileLang">
                  <Button
                    component="span"
                    color="primary"
                    variant="contained"
                    disabled={this.state.isUploading}
                    onClick={() => document.getElementById("customFileLang")?.click()}
                  >
                    {this.state.isUploading ? "Subiendo…" : "Subir"}
                  </Button>
                </label>

                <label htmlFor="demo">
                  <Button
                    component="span"
                    onClick={this.download}
                    color="primary"
                    style={{ width: "100%" }}
                    variant="contained"
                    disabled={this.state.isUploading}
                  >
                    Descargar ejemplo de carga
                  </Button>
                </label>
              </div>
            </form>
            {this.state.uvrLoads && (
              <div style={{ height: 400, width: "100%" }}>
                <DataGrid
                  rows={this.state.uvrLoads}
                  columns={columns}
                  rowCount={this.state.count}
                  pageSize={15}
                  getRowId={(row) => row._id}
                  onPageChange={(newPage) => setPage(newPage)}
                />
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };
}

export default uvr;